import { ReactNode, PointerEvent as ReactPointerEvent, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronLeft, Copy, ExternalLink, Image as ImageIcon, Loader2, Pencil, Smile, Trash2 } from "lucide-react";
import { ScreenHeader } from "./ScreenHeader";
import { PackLottie } from "./PackLottie";
import { BottomSheet } from "./BottomSheet";
import { DeletePackSheet, RenamePackSheet } from "./PackSheets";
import { openPackLink } from "../lib/telegram";
import { PackDetailData, PackInfo, PackStickerItem, copyText, errorText, haptic, isGone, packApi, packLink } from "../lib/packApi";

const HOLD_MS = 380; // press-and-hold before a sticker lifts
const MOVE_SLOP = 8; // finger travel that turns a press into a scroll
const CELL = 50; // lottie size inside a grid cell
const ACCENT = "var(--tg-theme-button-color, #3B82F6)";

interface Slot {
  l: number;
  t: number;
  r: number;
  b: number;
}

function moveItem<T>(list: T[], from: number, to: number): T[] {
  const next = list.slice();
  const [it] = next.splice(from, 1);
  next.splice(to, 0, it);
  return next;
}

export function PackDetail({
  pack,
  onBack,
  onChanged,
  onDeleted,
}: {
  pack: PackInfo;
  onBack: () => void;
  onChanged: (patch: Partial<PackInfo>) => void;
  onDeleted: (gone?: boolean) => void;
}) {
  const [data, setData] = useState<PackDetailData | null>(null);
  const [items, setItems] = useState<PackStickerItem[]>([]);
  const [title, setTitle] = useState(pack.title);
  const [coverId, setCoverId] = useState<string | null>(pack.cover_sticker_id);
  const [error, setError] = useState<string | null>(null);

  const [selId, setSelId] = useState<string | null>(null);
  const [selView, setSelView] = useState<"actions" | "emoji" | "confirm">("actions");
  const [emojiInput, setEmojiInput] = useState("");
  const [sheetBusy, setSheetBusy] = useState<string | null>(null);
  const [sheetError, setSheetError] = useState<string | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [toast, setToast] = useState<{ text: string; bad?: boolean } | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);

  const scrollRef = useRef<HTMLDivElement>(null);
  const cellEls = useRef(new Map<string, HTMLDivElement>());
  const itemsRef = useRef<PackStickerItem[]>([]);
  itemsRef.current = items;
  const prevRects = useRef<Map<string, DOMRect> | null>(null);
  const ghostRef = useRef<HTMLDivElement>(null);
  const ghostPos = useRef({ x: 0, y: 0 });
  const moveChain = useRef<Promise<unknown>>(Promise.resolve());
  const toastTimer = useRef<number | null>(null);

  const link = packLink(pack.name);

  // The pack was deleted elsewhere (e.g. the official bot): leave the screen, the parent drops it.
  const handleGone = (err: unknown): boolean => {
    if (!isGone(err)) return false;
    onDeleted(true);
    return true;
  };

  const flash = (text: string, bad = false) => {
    setToast({ text, bad });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 2300);
  };
  useEffect(() => () => void (toastTimer.current && window.clearTimeout(toastTimer.current)), []);

  const load = useCallback(
    async (opts: { fresh?: boolean; keepItems?: boolean } = {}) => {
      try {
        const d = await packApi.detail(pack.name, opts.fresh);
        setData(d);
        setTitle(d.title);
        setCoverId(d.cover_sticker_id);
        if (!opts.keepItems) setItems(d.stickers);
        setError(null);
        onChanged({ title: d.title, sticker_count: d.sticker_count, cover_sticker_id: d.cover_sticker_id });
      } catch (err) {
        if (handleGone(err)) return;
        setError(errorText(err, "تعذر تحميل الحزمة"));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pack.name],
  );
  useEffect(() => {
    void load();
  }, [load]);

  // ---------------------------------------------------------------- reorder (press, hold, drag)
  const press = useRef<{
    id: string;
    pointerId: number;
    x: number;
    y: number;
    timer: number | null;
    active: boolean;
  } | null>(null);
  const drag = useRef<{
    id: string;
    from: number;
    index: number;
    slots: Slot[];
    before: PackStickerItem[];
    px: number;
    py: number;
    raf: number | null;
  } | null>(null);

  const captureRects = () => {
    const m = new Map<string, DOMRect>();
    cellEls.current.forEach((el, id) => m.set(id, el.getBoundingClientRect()));
    prevRects.current = m;
  };

  // FLIP: after the grid re-orders, glide every cell from where it was to where it is.
  useLayoutEffect(() => {
    const before = prevRects.current;
    if (!before) return;
    prevRects.current = null;
    cellEls.current.forEach((el, id) => {
      const a = before.get(id);
      if (!a) return;
      const b = el.getBoundingClientRect();
      const dx = a.left - b.left;
      const dy = a.top - b.top;
      if (Math.abs(dx) < 1 && Math.abs(dy) < 1) return;
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: "translate(0, 0)" }], {
        duration: 190,
        easing: "cubic-bezier(.2,.8,.2,1)",
      });
    });
  }, [items]);

  const placeGhost = () => {
    const g = ghostRef.current;
    if (g) g.style.transform = `translate(${ghostPos.current.x - 34}px, ${ghostPos.current.y - 34}px) scale(1.12)`;
  };
  useLayoutEffect(placeGhost, [dragId]);

  const targetFor = (d: NonNullable<typeof drag.current>): number => {
    const sc = scrollRef.current;
    const top = sc ? sc.getBoundingClientRect().top : 0;
    const left = sc ? sc.getBoundingClientRect().left : 0;
    const x = d.px - left;
    const y = d.py - top + (sc ? sc.scrollTop : 0);
    let best = d.index;
    let bestDist = Infinity;
    for (let i = 0; i < d.slots.length; i++) {
      const s = d.slots[i];
      if (x >= s.l && x <= s.r && y >= s.t && y <= s.b) return i;
      const dist = Math.hypot((s.l + s.r) / 2 - x, (s.t + s.b) / 2 - y);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    return best;
  };

  const reorderToPointer = () => {
    const d = drag.current;
    if (!d) return;
    const target = targetFor(d);
    if (target !== d.index) {
      captureRects();
      setItems((prev) => moveItem(prev, d.index, target));
      d.index = target;
      haptic("select");
    }
  };

  const autoScroll = () => {
    const d = drag.current;
    const sc = scrollRef.current;
    if (!d || !sc) return;
    const r = sc.getBoundingClientRect();
    const edge = 72;
    let dy = 0;
    if (d.py < r.top + edge) dy = -Math.ceil(((r.top + edge - d.py) / edge) * 14);
    else if (d.py > r.bottom - edge) dy = Math.ceil(((d.py - (r.bottom - edge)) / edge) * 14);
    if (dy) {
      sc.scrollTop += dy;
      reorderToPointer();
    }
    d.raf = requestAnimationFrame(autoScroll);
  };

  // Window listeners must keep one identity from add to remove, whichever render added them.
  const live = useRef({ move: (_e: PointerEvent) => {}, up: (_e: PointerEvent) => {}, cancel: (_e: PointerEvent) => {} });
  const stable = useRef({
    move: (e: PointerEvent) => live.current.move(e),
    up: (e: PointerEvent) => live.current.up(e),
    cancel: (e: PointerEvent) => live.current.cancel(e),
    touch: (e: TouchEvent) => {
      if (drag.current) e.preventDefault();
    },
  }).current;

  const stopTracking = () => {
    window.removeEventListener("pointermove", stable.move);
    window.removeEventListener("pointerup", stable.up);
    window.removeEventListener("pointercancel", stable.cancel);
    window.removeEventListener("touchmove", stable.touch);
    if (press.current?.timer) window.clearTimeout(press.current.timer);
    press.current = null;
  };

  const startDrag = () => {
    const p = press.current;
    const sc = scrollRef.current;
    if (!p || !sc) return;
    const list = itemsRef.current;
    const from = list.findIndex((s) => s.sticker_id === p.id);
    if (from < 0) return;
    const sr = sc.getBoundingClientRect();
    const slots: Slot[] = list.map((s) => {
      const r = (cellEls.current.get(s.sticker_id) as HTMLElement).getBoundingClientRect();
      return { l: r.left - sr.left, r: r.right - sr.left, t: r.top - sr.top + sc.scrollTop, b: r.bottom - sr.top + sc.scrollTop };
    });
    p.active = true;
    drag.current = { id: p.id, from, index: from, slots, before: list, px: p.x, py: p.y, raf: null };
    ghostPos.current = { x: p.x, y: p.y };
    window.addEventListener("touchmove", stable.touch, { passive: false });
    setDragId(p.id);
    haptic("medium");
    drag.current.raf = requestAnimationFrame(autoScroll);
  };

  const finishDrag = (commit: boolean) => {
    const d = drag.current;
    drag.current = null;
    if (d?.raf) cancelAnimationFrame(d.raf);
    setDragId(null);
    if (!d) return;
    if (!commit) {
      captureRects();
      setItems(d.before);
      return;
    }
    if (d.index === d.from) return;
    moveChain.current = moveChain.current
      .then(() => packApi.move(pack.name, d.id, d.index))
      .then(() => load({ keepItems: true }))
      .catch(async (err) => {
        if (handleGone(err)) return;
        flash(errorText(err, "تعذر حفظ الترتيب"), true);
        await load({ fresh: true }); // show what Telegram really has
      });
  };

  function onWinMove(e: PointerEvent) {
    const p = press.current;
    if (!p || e.pointerId !== p.pointerId) return;
    if (!p.active) {
      if (Math.hypot(e.clientX - p.x, e.clientY - p.y) > MOVE_SLOP) stopTracking(); // it is a scroll
      return;
    }
    const d = drag.current;
    if (!d) return;
    d.px = e.clientX;
    d.py = e.clientY;
    ghostPos.current = { x: e.clientX, y: e.clientY };
    placeGhost();
    reorderToPointer();
  }

  function onWinUp(e: PointerEvent) {
    const p = press.current;
    if (!p || e.pointerId !== p.pointerId) return;
    const wasActive = p.active;
    const id = p.id;
    stopTracking();
    if (wasActive) finishDrag(true);
    else openSticker(id);
  }

  function onWinCancel(e: PointerEvent) {
    const p = press.current;
    if (!p || e.pointerId !== p.pointerId) return;
    const wasActive = p.active;
    stopTracking();
    if (wasActive) finishDrag(true);
  }

  live.current = { move: onWinMove, up: onWinUp, cancel: onWinCancel };

  const onCellDown = (e: ReactPointerEvent, id: string) => {
    if (press.current || sheetBusy) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    press.current = {
      id,
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      active: false,
      timer: window.setTimeout(startDrag, HOLD_MS),
    };
    window.addEventListener("pointermove", stable.move);
    window.addEventListener("pointerup", stable.up);
    window.addEventListener("pointercancel", stable.cancel);
  };

  // clean up if the screen is closed mid-press
  useEffect(
    () => () => {
      if (drag.current?.raf) cancelAnimationFrame(drag.current.raf);
      drag.current = null;
      stopTracking();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // ---------------------------------------------------------------- sticker sheet
  const sel = items.find((s) => s.sticker_id === selId) ?? null;

  function openSticker(id: string) {
    const s = itemsRef.current.find((x) => x.sticker_id === id);
    setSelId(id);
    setSelView("actions");
    setEmojiInput(s?.emoji ?? "");
    setSheetError(null);
    haptic("light");
  }
  const closeSticker = () => setSelId(null);

  const makeCover = async () => {
    if (!sel || sheetBusy) return;
    setSheetBusy("cover");
    setSheetError(null);
    try {
      await packApi.setCover(pack.name, sel.sticker_id);
      setCoverId(sel.sticker_id);
      onChanged({ cover_sticker_id: sel.sticker_id });
      haptic("light");
      closeSticker();
      flash("تم تعيين أيقونة الحزمة");
    } catch (err) {
      if (handleGone(err)) return;
      setSheetError(errorText(err, "تعذر تعيين الأيقونة"));
    } finally {
      setSheetBusy(null);
    }
  };

  const saveEmoji = async () => {
    if (!sel || sheetBusy) return;
    const next = emojiInput.trim();
    if (!next) return setSheetError("اكتب إيموجي واحد");
    if (next === sel.emoji) return closeSticker();
    setSheetBusy("emoji");
    setSheetError(null);
    try {
      const res = await packApi.setEmoji(pack.name, sel.sticker_id, next);
      setItems((prev) => prev.map((s) => (s.sticker_id === sel.sticker_id ? { ...s, emoji: res.emoji ?? next } : s)));
      haptic("light");
      closeSticker();
      flash("تم تعديل الإيموجي");
    } catch (err) {
      if (handleGone(err)) return;
      setSheetError(errorText(err, "تعذر تعديل الإيموجي"));
    } finally {
      setSheetBusy(null);
    }
  };

  const removeSticker = async () => {
    if (!sel || sheetBusy) return;
    setSheetBusy("delete");
    setSheetError(null);
    try {
      await packApi.removeSticker(pack.name, sel.sticker_id);
      const remaining = itemsRef.current.filter((s) => s.sticker_id !== sel.sticker_id);
      captureRects();
      setItems(remaining);
      haptic("medium");
      closeSticker();
      flash("تم حذف الملصق");
      void load({ keepItems: true });
    } catch (err) {
      if (handleGone(err)) return;
      setSheetError(errorText(err, "تعذر حذف الملصق"));
    } finally {
      setSheetBusy(null);
    }
  };

  const copy = async () => {
    await copyText(link);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  // ---------------------------------------------------------------- render
  const count = data ? items.length : pack.sticker_count;
  const lastOne = items.length <= 1;

  return (
    <div ref={scrollRef} className="h-full overflow-y-auto relative" style={{ overscrollBehavior: "contain" }}>
      <ScreenHeader title={title} onBack={onBack} />

      <div className="px-4 pt-3 pb-12 flex flex-col gap-5">
        {/* hero */}
        <div className="flex items-center gap-4">
          <div className="w-24 h-24 rounded-3xl glass-pill flex items-center justify-center shrink-0">
            {coverId ? <PackLottie id={coverId} size={72} /> : <div className="w-12 h-12 rounded-full bg-black/20" />}
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="font-semibold text-xl leading-snug break-words">{title}</h2>
            <p className="text-tg-hint text-sm mt-1">{count} ملصق</p>
            <p className="text-tg-hint text-xs mt-1 truncate" dir="ltr" style={{ textAlign: "right" }}>
              t.me/addemoji/{pack.name}
            </p>
          </div>
        </div>

        {/* actions */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => openPackLink(link)}
            className="flex-1 py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold flex items-center justify-center gap-2"
          >
            <ExternalLink size={17} strokeWidth={2.25} />
            فتح في تيليجرام
          </button>
          <button
            type="button"
            onClick={copy}
            className="glass-pill px-4 rounded-xl text-tg-text flex items-center justify-center gap-2 text-sm"
            aria-label="نسخ الرابط"
          >
            {copied ? <Check size={17} strokeWidth={2.5} /> : <Copy size={17} strokeWidth={2.25} />}
            {copied ? "تم النسخ" : "نسخ"}
          </button>
        </div>

        <div className="glass-card overflow-hidden">
          <button
            type="button"
            onClick={() => setRenameOpen(true)}
            className="w-full flex items-center gap-3 px-4 py-3.5 text-right"
          >
            <Pencil size={19} strokeWidth={2} className="text-tg-hint shrink-0" />
            <span className="flex-1">تعديل اسم الحزمة</span>
            <ChevronLeft size={18} className="text-tg-hint shrink-0" />
          </button>
        </div>

        {/* stickers */}
        <section>
          <div className="flex items-baseline justify-between mb-2.5 px-1">
            <h3 className="font-semibold">الملصقات</h3>
            <span className="text-tg-hint text-xs">{count}</span>
          </div>

          {error && <p className="text-red-400 text-sm mb-2">{error}</p>}

          <div className="glass-card p-2.5">
            {data === null && !error ? (
              <div className="grid grid-cols-5 gap-2">
                {Array.from({ length: 10 }).map((_, i) => (
                  <div key={i} className="aspect-square rounded-2xl bg-black/20 animate-pulse" />
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-5 gap-2">
                {items.map((s) => {
                  const dragging = dragId === s.sticker_id;
                  return (
                    <div
                      key={s.sticker_id}
                      ref={(el) => {
                        if (el) cellEls.current.set(s.sticker_id, el);
                        else cellEls.current.delete(s.sticker_id);
                      }}
                      data-sticker={s.sticker_id}
                      onPointerDown={(e) => onCellDown(e, s.sticker_id)}
                      onContextMenu={(e) => e.preventDefault()}
                      className="relative aspect-square rounded-2xl glass-pill flex items-center justify-center select-none cursor-pointer"
                      style={{
                        touchAction: "pan-y",
                        WebkitTouchCallout: "none",
                        WebkitUserSelect: "none",
                        opacity: dragging ? 0.35 : 1,
                        outline: dragging ? `2px dashed ${ACCENT}` : "none",
                        outlineOffset: -2,
                      }}
                    >
                      <PackLottie id={s.sticker_id} size={CELL} />
                      {s.emoji && (
                        <span className="absolute bottom-0.5 left-1 text-[10px] leading-none opacity-90">{s.emoji}</span>
                      )}
                      {s.sticker_id === coverId && (
                        <span
                          className="absolute top-1 right-1 w-3.5 h-3.5 rounded-full flex items-center justify-center"
                          style={{ background: ACCENT }}
                          title="أيقونة الحزمة"
                        >
                          <ImageIcon size={8} strokeWidth={3} color="#fff" />
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <p className="text-tg-hint text-xs mt-2 px-1 leading-relaxed">
            اضغط مطولاً على ملصق ثم اسحبه لتغيير مكانه. اضغط عليه مرة واحدة لخياراته.
          </p>
        </section>

        {/* danger */}
        <section>
          <h3 className="font-semibold mb-2.5 px-1">إجراءات</h3>
          <div className="glass-card overflow-hidden">
            <button
              type="button"
              onClick={() => setDeleteOpen(true)}
              className="w-full flex items-center gap-3 px-4 py-3.5 text-right text-red-400"
            >
              <Trash2 size={19} strokeWidth={2} className="shrink-0" />
              <span className="flex-1">حذف الحزمة</span>
            </button>
          </div>
        </section>
      </div>

      {/* sticker options */}
      <BottomSheet open={!!sel} onClose={closeSticker} locked={!!sheetBusy}>
        {sel && (
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-20 h-20 rounded-2xl glass-pill flex items-center justify-center shrink-0">
                <PackLottie id={sel.sticker_id} size={64} />
              </div>
              <div className="min-w-0">
                <p className="font-semibold">ملصق رقم {items.findIndex((s) => s.sticker_id === sel.sticker_id) + 1}</p>
                <p className="text-tg-hint text-sm mt-0.5">الإيموجي: {sel.emoji || "—"}</p>
              </div>
            </div>

            {selView === "actions" && (
              <div className="glass-card overflow-hidden">
                <SheetRow
                  icon={<ImageIcon size={19} strokeWidth={2} />}
                  label="تعيين كأيقونة للحزمة"
                  busy={sheetBusy === "cover"}
                  onClick={makeCover}
                />
                <SheetRow
                  icon={<Smile size={19} strokeWidth={2} />}
                  label="تعديل الإيموجي"
                  onClick={() => {
                    setSelView("emoji");
                    setSheetError(null);
                  }}
                />
                <SheetRow
                  icon={<Trash2 size={19} strokeWidth={2} />}
                  label="حذف الملصق"
                  danger
                  disabled={lastOne}
                  onClick={() => {
                    setSelView("confirm");
                    setSheetError(null);
                  }}
                />
                {lastOne && (
                  <p className="text-tg-hint text-xs px-4 pb-3">هذا آخر ملصق في الحزمة، لحذفه احذف الحزمة كاملة.</p>
                )}
              </div>
            )}

            {selView === "emoji" && (
              <div>
                <input
                  type="text"
                  value={emojiInput}
                  maxLength={8}
                  dir="ltr"
                  autoFocus
                  onChange={(e) => setEmojiInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && saveEmoji()}
                  placeholder="😀"
                  className="w-full rounded-xl glass-pill px-4 py-3 text-base text-center outline-none placeholder:text-tg-hint"
                />
                <div className="flex gap-3 mt-3">
                  <button
                    type="button"
                    disabled={!!sheetBusy}
                    onClick={() => setSelView("actions")}
                    className="flex-1 glass-pill py-3 rounded-xl text-tg-text"
                  >
                    رجوع
                  </button>
                  <button
                    type="button"
                    disabled={!!sheetBusy || !emojiInput.trim()}
                    onClick={saveEmoji}
                    className="flex-1 py-3 rounded-xl bg-tg-button text-tg-buttonText font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
                  >
                    {sheetBusy === "emoji" && <Loader2 size={16} className="animate-spin" />}
                    حفظ
                  </button>
                </div>
              </div>
            )}

            {selView === "confirm" && (
              <div>
                <p className="text-sm text-tg-hint text-center mb-3">سيُحذف هذا الملصق من الحزمة على تيليجرام.</p>
                <div className="flex gap-3">
                  <button
                    type="button"
                    disabled={!!sheetBusy}
                    onClick={() => setSelView("actions")}
                    className="flex-1 glass-pill py-3 rounded-xl text-tg-text"
                  >
                    إلغاء
                  </button>
                  <button
                    type="button"
                    disabled={!!sheetBusy}
                    onClick={removeSticker}
                    className="flex-1 py-3 rounded-xl font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: "#c0392b", color: "#fff" }}
                  >
                    {sheetBusy === "delete" && <Loader2 size={16} className="animate-spin" />}
                    حذف
                  </button>
                </div>
              </div>
            )}

            {sheetError && <p className="text-red-400 text-sm mt-3 text-center">{sheetError}</p>}
          </div>
        )}
      </BottomSheet>

      <RenamePackSheet
        open={renameOpen}
        name={pack.name}
        title={title}
        onClose={() => setRenameOpen(false)}
        onDone={(t) => {
          setTitle(t);
          onChanged({ title: t });
          flash("تم تغيير الاسم");
        }}
        onGone={() => onDeleted(true)}
      />
      <DeletePackSheet open={deleteOpen} name={pack.name} title={title} onClose={() => setDeleteOpen(false)} onDone={() => onDeleted()} />

      {/* lifted sticker follows the finger */}
      {dragId &&
        createPortal(
          <div
            ref={ghostRef}
            className="fixed left-0 top-0 z-[60] pointer-events-none rounded-2xl flex items-center justify-center"
            style={{
              width: 68,
              height: 68,
              background: "var(--tg-theme-secondary-bg-color, #1b1f27)",
              boxShadow: "0 14px 32px rgba(0,0,0,.45)",
              border: `1px solid ${ACCENT}`,
            }}
          >
            <PackLottie id={dragId} size={52} />
          </div>,
          document.body,
        )}

      {toast &&
        createPortal(
          <div
            className="fixed left-1/2 z-[70] -translate-x-1/2 px-4 py-2.5 rounded-full text-sm shadow-lg"
            style={{
              bottom: "max(24px, env(safe-area-inset-bottom))",
              background: toast.bad ? "#8e2b22" : "rgba(30,34,43,.96)",
              color: "#fff",
              border: "1px solid rgba(255,255,255,.08)",
            }}
            role="status"
          >
            {toast.text}
          </div>,
          document.body,
        )}
    </div>
  );
}

function SheetRow({
  icon,
  label,
  onClick,
  danger,
  disabled,
  busy,
}: {
  icon: ReactNode;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={`w-full flex items-center gap-3 px-4 py-3.5 text-right disabled:opacity-40 ${danger ? "text-red-400" : ""}`}
    >
      <span className={`shrink-0 ${danger ? "" : "text-tg-hint"}`}>
        {busy ? <Loader2 size={19} className="animate-spin" /> : icon}
      </span>
      <span className="flex-1">{label}</span>
    </button>
  );
}
